import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { DetailedChangeset } from 'ember-changeset/types';
import { and, not } from 'ember-truth-helpers';
import DateInput from 'garaje/components/date-input';
import isRemovedString from 'garaje/helpers/is-removed-string';
import removeYour from 'garaje/helpers/remove-your';
import removedString from 'garaje/helpers/removed-string';
import type SignInFieldModel from 'garaje/models/sign-in-field';
import { convertFromISO, convertToISO } from 'garaje/utils/date-format';
import { DateFieldFormat } from 'garaje/utils/enums';

function hasDigits(value: string): boolean {
  return value.replace(/\D/g, '').length > 0;
}

interface EntriesDateFieldSignature {
  Args: {
    changeset: DetailedChangeset<SignInFieldModel>;
    update: (changeset: DetailedChangeset<SignInFieldModel>) => void;
    disabled?: boolean;
    label?: string;
  };
  Element: HTMLDivElement;
}

export default class EntriesDateField extends Component<EntriesDateFieldSignature> {
  @tracked isFocused = false;
  @tracked private _displayValue: string | null = null;

  get displayValue(): string {
    if (this._displayValue !== null) {
      return this._displayValue;
    }
    const existingValue = this.args.changeset.value;
    if (existingValue) {
      return convertFromISO(existingValue, this.dateFormat) ?? existingValue;
    }
    return '';
  }

  get dateFormat(): DateFieldFormat {
    return this.args.changeset.dateFormat ?? DateFieldFormat.MM_DD_YYYY;
  }

  get isRequired(): boolean {
    return this.args.changeset.required && !this.args.changeset.allowVisitorRespondents;
  }

  get errorValidation(): string[] {
    const errors = <string[]>this.args.changeset.error?.['value']?.['validation'] || [];
    return errors.filter((error) => error !== 'Please enter a valid date');
  }

  @action
  handleChange(value: string, isValid: boolean): void {
    this.isFocused = true;
    this._displayValue = value;
    if (isValid) {
      this.args.changeset.value = convertToISO(value, this.dateFormat);
    } else {
      this.args.changeset.value = hasDigits(value) ? value : '';
    }
    this.args.update(this.args.changeset);
  }

  @action
  handleBlur(value: string, isValid: boolean): void {
    this.isFocused = false;
    if (isValid) {
      this.args.changeset.value = convertToISO(value, this.dateFormat);
    } else {
      this.args.changeset.value = hasDigits(value) ? value : '';
    }
    this._displayValue = null;
    this.args.update(this.args.changeset);
    void this.args.changeset.validate();
  }

  <template>
    <div data-test-flow-field={{@changeset.name}} ...attributes>
      <label>
        {{#if @label}}
          {{@label}}
        {{else}}
          {{removeYour (if @changeset.localized @changeset.localized @changeset.name)}}
        {{/if}}
        {{#if this.isRequired}}
          <span class="requiredField" data-test-required-field></span>
        {{/if}}
        <span class="text-carbon-50 text-xs ml-1" data-test-date-format-hint>({{this.dateFormat}})</span>
      </label>
      {{#if (isRemovedString @changeset.value)}}
        <input value={{removedString @changeset.value}} disabled={{true}} type="text" />
      {{else}}
        <DateInput
          @value={{this.displayValue}}
          @format={{this.dateFormat}}
          @disabled={{@disabled}}
          @required={{this.isRequired}}
          @showError={{if (and this.errorValidation (not this.isFocused)) true}}
          @onChange={{this.handleChange}}
          @onBlur={{this.handleBlur}}
        />
        {{#unless this.isFocused}}
          {{#each this.errorValidation as |errorKey|}}
            <div class="bottom-error pt-1" data-test-error>{{errorKey}}</div>
          {{/each}}
        {{/unless}}
      {{/if}}
    </div>
  </template>
}
